<template> 
  <a
    v-if="(file.type !== 'image' || !file.resolution) && !gallery"
    :href="file.download_url.original"
    class="Table__DownloadPanel cursor-pointer"
    :class="isPill ? 'Table__DownloadPanel-Pill flex items-center justify-center rounded-full bg-bc-blue text-white' : 'text-gray-400 hover:text-gray-500'"
    @click.stop.prevent="downloadFile"
  >
    <span 
      v-if="isPill"
      class="text-center truncate"
    >
      .{{ file.ext }}
    </span>
    <span 
      v-else
      class="hidden sm:inline"
    >
      {{ formatBytes(file.size) }}
    </span>
    <aw-icon
      icon="download"
      class="w-5 h-5"
      :class="isPill ? 'ml-auto' : 'mr-1.5 cursor-pointer | MoreVisibleIcon text-gray-400 ml-1'"
    />
  </a>
  <a
    v-else-if="(file.type !== 'image' || !file.resolution) && gallery && !zipEnabled"
    :class="isPill ? 'Table__DownloadPanel-PillIcon flex items-center justify-center rounded-full bg-bc-blue cursor-pointer text-white' : 'hover:text-gray-200'"
    :href="file.download_url.original"
    @click.stop.prevent="downloadFile"
  >
    <aw-icon 
      icon="download" 
      class="w-5 h-5"
    />
  </a>

  <!-- Download dropdown  -->
  <aw-dropdown v-else placement="bottom-start" :offset="[0, 8]" class="">
    <template v-slot:btn="{ toggle }">
      <a
        v-if="!gallery"
        @click.stop.prevent="toggle"
        href=""
        class="Table__DownloadPanel cursor-pointer"
        :class="isPill ? 'Table__DownloadPanel-Pill flex items-center justify-center rounded-full bg-bc-blue text-white' : 'text-gray-400 hover:text-gray-500'"
      >
        <span 
          v-if="isPill"
          class="text-center truncate"
        >
          .{{ file.ext }}
        </span>
        <span 
          v-else
          class="hidden sm:inline"
        >
          {{ formatBytes(file.size) }}
        </span>
        <aw-icon
          icon="download"
          class="w-5 h-5"
          :class="isPill ? 'ml-auto' : 'mr-1.5 cursor-pointer | MoreVisibleIcon text-gray-400 ml-1'"
        />
      </a>
      <a 
        v-else
        :class="isPill ? 'Table__DownloadPanel-PillIcon flex items-center justify-center rounded-full bg-bc-blue cursor-pointer text-white' : 'hover:text-gray-200'"
        @click.stop.prevent="toggle" 
        href="" 
        @click.stop
      >
        <aw-icon
          icon="download"
          class="w-5 h-5"
        />
      </a>
    </template>
    <template v-slot:dropdown="{ opened, dropdown, close }">
      <div
        v-show="opened"
        :ref="dropdown"
        class="Dropdown--Hover absolute rounded-md bg-white max-w-xs text-sm top-full right-0 shadow-md z-10"
      >
        <nav class="p-0 text-left text-sm">
          <template v-if="file.antivir_ok">
            <a
              v-for="(resolution, size) in file.resolution"
              :key="size"
              class="block py-1.5 px-3 text-gray-600 hover:text-gray-800"
              target="_blank"
              :href="file.download_url[size]"
              @click="trackDownloadSize(size, close)"
            >
              <aw-icon
                icon="download"
                class="w-5 h-5 "
              />
              {{ $t(size) }}
              <span class="text-gray-400"
                >{{ resolution[0] }}x{{ resolution[1] }}px</span
              >
            </a>
            <a
              v-if="!file.resolution"
              class="block py-1.5 px-3 text-gray-600 hover:text-gray-800"
              target="_blank"
              :href="file.download_url.original"
              @click.stop.prevent="downloadFile"
            >
              <aw-icon
                icon="download"
                class="w-5 h-5 "
              />
              {{ file.name }}
            </a>
          </template>
          <a
            v-if="!file.antivir_ok && !file.antivir_tested"
            class="block py-1.5 px-3 text-gray-600 hover:text-gray-800"
            target="_blank"
            :href="file.download_url.original"
            @click.stop.prevent="downloadFile"
          >
            <aw-icon
              icon="download"
              class="w-5 h-5 "
            />
            {{ $t('original') }}
          </a>
          <a
            v-if="zipEnabled"
            key="zip"
            class="block py-1.5 px-3 text-gray-600 hover:text-gray-800"
            href=""
            @click.prevent.stop="downloadZip(close)"
          >
            <aw-icon
              :icon="zipLoading ? 'loading' : 'download'"
              class="w-5 h-5 "
              :class="{'loading': zipLoading}"
            />
            {{ $t('Download gallery as ZIP') }}
          </a>
        </nav>
      </div>
    </template>
  </aw-dropdown>
</template>
<script>
import { saveAs } from 'file-saver'
import { DocumentApi } from '../../test-api/index.ts'
import { trackEvent } from '../../gtm'
import useBcDocumentStore from '@/frontapp/store/bcDocument'
import { showAlert } from "../BcAlert";
import { showConfirm } from "../BcConfirm";
import handleApiError from '../../utils/handleApiError'

const documentApi = new DocumentApi({}, '/api/v2')

export default {
  props: {
    gallery: {
      type: Boolean,
      default: false
    },
    isPill: {
      type: Boolean,
      default: false
    },
    file: Object,
    zipElements: {
      type: Array,
      default: null
    },
  },
  data() {
    return {
      zipLoading: false,
    }
  },
  computed: {
    zipEnabled() {
      return this.zipElements && this.zipElements.length > 1
    },
    downloadableFiles() {
      return this.zipElements.filter(e => e.data.can_download && e.file.can_download)
    },
    antivirNotPassedFiles() {
      return this.downloadableFiles.filter((e) => {
        return (!e.file.antivir_ok && !e.file.antivir_tested)
      })
    },
    antivirInfectedFiles() {
      return this.downloadableFiles.filter(e => e.file.virus_found)
    }
  },
  methods: {
    async downloadZip(close) {
      if (!await this.avCheck(true)) {
        return
      }

      this.zipLoading = true
      const bcDocumentStore = useBcDocumentStore()
      if (bcDocumentStore.documentRev) {
        const requestZipResponse = await handleApiError(() => documentApi.fileRequestZipDocumentRev(
          bcDocumentStore.document.id,
          bcDocumentStore.documentRev,
          'original',
          this.downloadableFiles.map((e) => e.id)
        ))

        saveAs('/api/v2/zip/' + requestZipResponse.data.hash, requestZipResponse.data.file_name + '.zip')
      } else if (this.$route.matched[0].name !== 'BCSharedDocument') {
        const requestZipResponse = await handleApiError(() => documentApi.fileRequestZipDocument(
          bcDocumentStore.document.id,
          'original',
          this.downloadableFiles.map((e) => e.id)
        ))

        saveAs('/api/v2/zip/' + requestZipResponse.data.hash, requestZipResponse.data.file_name + '.zip')
      } else {
        const requestZipResponse = await handleApiError(() => documentApi.fileRequestZipSharedDocument(
          bcDocumentStore.document.id,
          'original',
          this.downloadableFiles.map((e) => e.id)
        ))

        saveAs('/api/v2/zip/' + requestZipResponse.data.hash, requestZipResponse.data.file_name + '.zip')
      }
      this.zipLoading = false
      trackEvent(this.$route.matched[0].name, 'downloadZip', bcDocumentStore.document.id)
      close()
    },
    trackDownloadSize(size, close) {
      if (size === 'original') {
        trackEvent(this.$route.matched[0].name, 'downloadFile', this.file.id)
      } else {
        trackEvent(this.$route.matched[0].name, 'downloadSize' + size.charAt(0).toUpperCase() + size.slice(1), this.file.id)
      }
      close()
    },
    async avCheck(zip = false) {
      const virusFound = zip === false
        ? this.file.virus_found
        : this.antivirInfectedFiles.length > 0

      if (virusFound) {
        await showAlert({
          header: zip
            ? this.$t('Cannot download virus infected files.')
            : this.$t('Cannot download virus infected file.'),
        })
        return false
      }

      const antivirNotPassed = zip === false
        ? (!this.file.antivir_ok && !this.file.antivir_tested)
        : this.antivirNotPassedFiles.length > 0

      if (antivirNotPassed) {
        return await showConfirm({
          header: zip && this.downloadableFiles > 1
            ? this.$t('Do you really want to download antivir untested files?')
            : this.$t('Do you really want to download antivir untested file?'),
          
          text: zip && this.downloadableFiles > 1
            ? this.$t('Some of the selected files haven\'t been tested with antivirus yet.')
            : this.$t('Selected file haven\'t been tested with antivirus yet.'),
        })
      }

      return true
    },
    async downloadFile() {
      if (!await this.avCheck()) {
        return
      }

      saveAs(this.file.download_url.original)
      trackEvent(this.$route.matched[0].name, 'downloadFile', this.file.id)
    },
  }
}
</script>

<style lang="scss">
.Table__DownloadPanel-Pill {
  padding: 0.4rem 0.4rem 0.4rem 1.2rem;
  transition: 200ms;
  width: 5.5rem;

  span {
    min-width: 32px;
  }

  &:hover {
    transform: translateY(-2px);
  }
}

.Table__DownloadPanel-PillIcon {
  padding: 0.125rem 0;
  transition: 200ms;

  &:hover {
    transform: translateY(-2px);
  }
}
</style>
